<template>
  <div class="feed-filters-wrap">
    <!-- Filter panel -->
    <transition name="filters-slide">
      <div v-if="open" class="feed-filters">
        <div class="feed-filters__header">
          <h3 class="feed-filters__title">Filter articles</h3>
          <button class="feed-filters__close" @click="$emit('close')">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="feed-filters__body" :class="{ 'feed-filters__body--loading': filtersLoading }">
          <div
            v-for="group in filterGroups"
            :key="group.id"
            class="ff-group"
          >
            <button
              class="ff-group__header"
              @click="toggleGroup(group.id)"
            >
              <span class="ff-group__label">
                {{ group.label }}
                <span v-if="selectedCount(group.category)" class="ff-group__count">
                  {{ selectedCount(group.category) }}
                </span>
              </span>
              <svg
                class="ff-group__chevron"
                :class="{ 'ff-group__chevron--open': expandedGroups[group.id] }"
                width="12" height="12" viewBox="0 0 12 8" fill="none"
              >
                <path d="M1 1.5l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <transition name="group-expand">
              <div v-if="expandedGroups[group.id]" class="ff-group__body">
                <div v-if="!group.children || !group.children.length" class="ff-group__empty">
                  {{ hasActiveFilters ? "There are no options available for your current selected Jurisdiction." : "No options available" }}
                </div>
                <template v-else>
                  <input
                    v-if="group.children.length > 8"
                    v-model="searchTerms[group.id]"
                    type="text"
                    class="ff-group__search"
                    :placeholder="`Search ${group.label.toLowerCase()}...`"
                  />
                  <div class="ff-group__options">
                    <div
                      v-for="opt in filteredOptions(group)"
                      :key="opt.id"
                      class="ff-item"
                    >
                      <div class="ff-item__row">
                        <button
                          v-if="opt.children && opt.children.length"
                          class="ff-item__expand"
                          @click.stop="toggleNode(group.id, opt.id)"
                        >
                          <svg
                            class="ff-item__expand-icon"
                            :class="{ 'ff-item__expand-icon--open': isNodeExpanded(group.id, opt.id) }"
                            width="8" height="8" viewBox="0 0 8 8" fill="none"
                          >
                            <path d="M2 1l4 3-4 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </button>
                        <span v-else class="ff-item__expand-spacer" />
                        <label class="ff-item__label">
                          <input
                            type="checkbox"
                            :checked="isSelected(group.category, opt.id)"
                            :disabled="isDisabledByParent(group.category, opt.id)"
                            class="ff-item__checkbox"
                            @change="toggleOption(group.category, opt.id, opt.label, opt.children)"
                          />
                          <span class="ff-item__text" :class="{ 'ff-item__text--disabled': isDisabledByParent(group.category, opt.id) }">{{ opt.label }}</span>
                        </label>
                      </div>
                      <!-- Children (level 1) -->
                      <transition name="group-expand">
                        <div
                          v-if="opt.children && opt.children.length && isNodeExpanded(group.id, opt.id)"
                          class="ff-item__children"
                        >
                          <div
                            v-for="child in opt.children"
                            :key="child.id"
                            class="ff-item"
                          >
                            <div class="ff-item__row">
                              <button
                                v-if="child.children && child.children.length"
                                class="ff-item__expand"
                                @click.stop="toggleNode(group.id, child.id)"
                              >
                                <svg
                                  class="ff-item__expand-icon"
                                  :class="{ 'ff-item__expand-icon--open': isNodeExpanded(group.id, child.id) }"
                                  width="8" height="8" viewBox="0 0 8 8" fill="none"
                                >
                                  <path d="M2 1l4 3-4 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                              </button>
                              <span v-else class="ff-item__expand-spacer" />
                              <label class="ff-item__label">
                                <input
                                  type="checkbox"
                                  :checked="isSelected(group.category, child.id) || isDisabledByParent(group.category, child.id)"
                                  :disabled="isDisabledByParent(group.category, child.id)"
                                  class="ff-item__checkbox"
                                  @change="toggleOption(group.category, child.id, child.label, child.children)"
                                />
                                <span class="ff-item__text" :class="{ 'ff-item__text--disabled': isDisabledByParent(group.category, child.id) }">{{ child.label }}</span>
                              </label>
                            </div>
                            <!-- Children (level 2) -->
                            <transition name="group-expand">
                              <div
                                v-if="child.children && child.children.length && isNodeExpanded(group.id, child.id)"
                                class="ff-item__children"
                              >
                                <div
                                  v-for="grandchild in child.children"
                                  :key="grandchild.id"
                                  class="ff-item"
                                >
                                  <div class="ff-item__row">
                                    <span class="ff-item__expand-spacer" />
                                    <label class="ff-item__label">
                                      <input
                                        type="checkbox"
                                        :checked="isSelected(group.category, grandchild.id) || isDisabledByParent(group.category, grandchild.id)"
                                        :disabled="isDisabledByParent(group.category, grandchild.id)"
                                        class="ff-item__checkbox"
                                        @change="toggleOption(group.category, grandchild.id, grandchild.label, null)"
                                      />
                                      <span class="ff-item__text" :class="{ 'ff-item__text--disabled': isDisabledByParent(group.category, grandchild.id) }">{{ grandchild.label }}</span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </transition>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </template>
              </div>
            </transition>
          </div>
          <div v-if="!filterGroups.length" class="feed-filters__loading">
            Loading filters...
          </div>
        </div>
        <div class="feed-filters__footer">
          <button class="feed-filters__apply" @click="applyFilters">
            Apply filters
            <span v-if="totalSelected" class="feed-filters__apply-count">({{ totalSelected }})</span>
          </button>
          <button
            v-if="hasActiveFilters"
            class="feed-filters__reset"
            @click="resetFilters"
          >
            Clear all
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "FeedFilters",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    filterData: {
      type: [Array, Object],
      default: () => [],
    },
    initialSelections: {
      type: Object,
      default: () => ({}),
    },
    initialLabels: {
      type: Object,
      default: () => ({}),
    },
    filtersLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedGroups: {},
      expandedNodes: {},
      selections: {},
      selectionLabels: {},
      initialised: false,
      // Tracks which tids are parents (have children selected implicitly)
      parentSelections: {},
      searchTerms: {},
    };
  },
  computed: {
    filterGroups() {
      if (Array.isArray(this.filterData)) {
        return this.filterData;
      }
      return [];
    },
    hasActiveFilters() {
      return this.totalSelected > 0;
    },
    totalSelected() {
      let count = 0;
      Object.values(this.selections).forEach((arr) => {
        if (arr) count += arr.length;
      });
      return count;
    },
    activeChips() {
      const chips = [];
      Object.keys(this.selections).forEach((category) => {
        const tids = this.selections[category] || [];
        tids.forEach((tid) => {
          const key = `${category}:${tid}`;
          chips.push({
            category,
            id: tid,
            label: this.selectionLabels[key] || `${category} ${tid}`,
          });
        });
      });
      return chips;
    },
    // Build the effective tids: explicit selections + all their descendant leaf tids
    effectiveSelections() {
      const result = {};
      Object.keys(this.selections).forEach((category) => {
        const tids = this.selections[category] || [];
        if (!tids.length) return;
        const effective = new Set();
        const group = this.filterGroups.find((g) => g.category === category);
        tids.forEach((tid) => {
          if (group) {
            const node = this.findNode(group.children, tid);
            if (node && node.children && node.children.length) {
              // Parent: collect all descendant leaf tids
              this.collectLeafTids(node.children, effective);
            } else {
              // Leaf: just add itself
              effective.add(tid);
            }
          } else {
            effective.add(tid);
          }
        });
        result[category] = Array.from(effective);
      });
      return result;
    },
  },
  watch: {
    filterData: {
      handler() {
        this.applyInitialSelections();
      },
      deep: true,
    },
    initialSelections: {
      handler() {
        this.applyInitialSelections();
      },
      deep: true,
    },
    open(isOpen) {
      if (isOpen && this.initialised) {
        this.syncFromStore();
      }
    },
  },
  methods: {
    syncFromStore() {
      const sel = this.initialSelections || {};
      const labels = this.initialLabels || {};
      this.selections = {};
      this.selectionLabels = {};
      Object.entries(sel).forEach(([category, tids]) => {
        if (!tids || !tids.length) return;
        this.$set(this.selections, category, [...tids]);
        tids.forEach((tid) => {
          const key = `${category}:${tid}`;
          if (labels[key]) {
            this.$set(this.selectionLabels, key, labels[key]);
          }
        });
      });
    },
    applyInitialSelections() {
      if (this.initialised) return;
      const sel = this.initialSelections;
      if (!sel || !Object.keys(sel).length) return;

      // Copy initial selections
      Object.entries(sel).forEach(([category, tids]) => {
        if (!tids || !tids.length) return;
        this.$set(this.selections, category, [...tids]);
        // Use stored labels first, then resolve from filter data
        tids.forEach((tid) => {
          const storedLabel = this.initialLabels[`${category}:${tid}`];
          if (storedLabel) {
            this.$set(this.selectionLabels, `${category}:${tid}`, storedLabel);
          } else if (this.filterGroups.length) {
            const group = this.filterGroups.find((g) => g.category === category);
            if (group) {
              const node = this.findNode(group.children, tid);
              if (node) {
                this.$set(this.selectionLabels, `${category}:${tid}`, node.label);
              }
            }
          }
        });
      });
      this.initialised = true;
    },
    toggleGroup(id) {
      this.$set(this.expandedGroups, id, !this.expandedGroups[id]);
    },
    toggleNode(groupId, nodeId) {
      const key = `${groupId}:${nodeId}`;
      this.$set(this.expandedNodes, key, !this.expandedNodes[key]);
    },
    isNodeExpanded(groupId, nodeId) {
      return !!this.expandedNodes[`${groupId}:${nodeId}`];
    },
    isSelected(category, tid) {
      return this.selections[category] && this.selections[category].includes(tid);
    },
    isDisabledByParent(category, tid) {
      // Check if any ancestor of this tid is explicitly selected
      const selectedTids = this.selections[category] || [];
      if (!selectedTids.length) return false;
      const group = this.filterGroups.find((g) => g.category === category);
      if (!group) return false;
      // Walk the tree: if any selected tid is an ancestor of this tid, disable it
      for (const selectedTid of selectedTids) {
        if (selectedTid === tid) continue;
        const node = this.findNode(group.children, selectedTid);
        if (node && this.isDescendant(node, tid)) {
          return true;
        }
      }
      return false;
    },
    isDescendant(parentNode, tid) {
      if (!parentNode.children) return false;
      for (const child of parentNode.children) {
        if (child.id === tid) return true;
        if (this.isDescendant(child, tid)) return true;
      }
      return false;
    },
    findNode(nodes, tid) {
      if (!nodes) return null;
      for (const node of nodes) {
        if (node.id === tid) return node;
        const found = this.findNode(node.children, tid);
        if (found) return found;
      }
      return null;
    },
    collectLeafTids(nodes, set) {
      for (const node of nodes) {
        if (node.children && node.children.length) {
          this.collectLeafTids(node.children, set);
        } else {
          set.add(node.id);
        }
      }
    },
    selectedCount(category) {
      return (this.selections[category] || []).length;
    },
    toggleOption(category, tid, label, children) {
      if (!this.selections[category]) {
        this.$set(this.selections, category, []);
      }
      const idx = this.selections[category].indexOf(tid);
      const key = `${category}:${tid}`;
      if (idx > -1) {
        // Deselecting
        this.selections[category].splice(idx, 1);
        this.$delete(this.selectionLabels, key);
      } else {
        // Selecting — also remove any explicitly selected children (parent covers them)
        if (children && children.length) {
          this.removeDescendantSelections(category, children);
        }
        this.selections[category].push(tid);
        this.$set(this.selectionLabels, key, label);
      }
      this.$emit("filter-changed", {
        effectiveSelections: JSON.parse(JSON.stringify(this.effectiveSelections)),
        rawSelections: JSON.parse(JSON.stringify(this.selections)),
      });
    },
    removeDescendantSelections(category, children) {
      for (const child of children) {
        const idx = (this.selections[category] || []).indexOf(child.id);
        if (idx > -1) {
          this.selections[category].splice(idx, 1);
          this.$delete(this.selectionLabels, `${category}:${child.id}`);
        }
        if (child.children && child.children.length) {
          this.removeDescendantSelections(category, child.children);
        }
      }
    },
    removeChip(chip) {
      if (this.selections[chip.category]) {
        const idx = this.selections[chip.category].indexOf(chip.id);
        if (idx > -1) {
          this.selections[chip.category].splice(idx, 1);
          this.$delete(this.selectionLabels, `${chip.category}:${chip.id}`);
        }
      }
      this.$emit("apply", {
        selections: this.effectiveSelections,
        queryParams: this.buildQueryParams(),
      });
    },
    filteredOptions(group) {
      const term = (this.searchTerms[group.id] || "").toLowerCase();
      if (!term) return group.children;
      return this.filterTree(group.children, term);
    },
    filterTree(nodes, term) {
      const result = [];
      for (const node of nodes) {
        const nameMatch = (node.label || "").toLowerCase().includes(term);
        const childMatches = node.children ? this.filterTree(node.children, term) : [];
        if (nameMatch || childMatches.length) {
          result.push({
            ...node,
            children: nameMatch ? node.children : childMatches,
          });
        }
      }
      return result;
    },
    buildQueryParams() {
      const effective = this.effectiveSelections;
      const parts = [];
      Object.keys(effective).forEach((category) => {
        const tids = effective[category];
        if (tids && tids.length) {
          const safeCategory = encodeURIComponent(category);
          tids.forEach((tid) => {
            parts.push(`filter[${safeCategory}][]=${encodeURIComponent(tid)}`);
          });
        }
      });
      return parts.join("&");
    },
    applyFilters() {
      this.$emit("apply", {
        selections: this.effectiveSelections,
        queryParams: this.buildQueryParams(),
        rawSelections: JSON.parse(JSON.stringify(this.selections)),
        rawLabels: JSON.parse(JSON.stringify(this.selectionLabels)),
      });
    },
    resetFilters() {
      this.selections = {};
      this.selectionLabels = {};
      this.initialised = false;
      this.$emit("apply", {
        selections: {},
        queryParams: "",
        rawSelections: {},
        rawLabels: {},
      });
      this.$emit("filter-changed", {
        effectiveSelections: {},
        rawSelections: {},
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

.filters-slide-enter-active,
.filters-slide-leave-active {
  transition: all 0.25s ease;
}
.filters-slide-enter,
.filters-slide-leave-to {
  opacity: 0;
  max-height: 0;
  overflow: hidden;
}
.filters-slide-enter-to,
.filters-slide-leave {
  opacity: 1;
  max-height: 900px;
}

.group-expand-enter-active,
.group-expand-leave-active {
  transition: all 0.2s ease;
}
.group-expand-enter,
.group-expand-leave-to {
  opacity: 0;
  max-height: 0;
  overflow: hidden;
}
.group-expand-enter-to,
.group-expand-leave {
  opacity: 1;
  max-height: 600px;
}

.feed-filters-active {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #ecedf0;

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex: 1;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: rgba($corporateBlue, 0.06);
    color: $corporateBlue;
    border-radius: 4px;
    font-size: 1.15rem;
    font-weight: 600;
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chip-remove {
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    color: $corporateBlue;
    padding: 0;
    opacity: 0.5;
    transition: opacity 0.12s ease;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }
  }

  &__clear {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.15rem;
    font-weight: 600;
    color: $light_gray;
    white-space: nowrap;
    padding: 4px 0;
    transition: color 0.12s ease;
    flex-shrink: 0;

    &:hover {
      color: $text;
    }
  }
}

.feed-filters {
  background: $white;
  border: 1px solid #ecedf0;
  border-radius: 8px;
  margin-bottom: 24px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-bottom: 1px solid #ecedf0;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: $text;
    margin: 0;
  }

  &__close {
    background: none;
    border: none;
    cursor: pointer;
    color: $light_gray;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    transition: color 0.15s ease;

    &:hover {
      color: $text;
    }
  }

  &__body {
    padding: 4px 20px;
    max-height: 480px;
    overflow-y: auto;
    scrollbar-width: thin;
    position: relative;
    transition: opacity 0.2s ease;

    &--loading {
      opacity: 0.45;
      pointer-events: none;
    }
  }

  &__loading {
    padding: 24px 0;
    text-align: center;
    color: $light_gray;
    font-size: 1.3rem;
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    border-top: 1px solid #ecedf0;
    background: #fafbfc;
  }

  &__apply {
    padding: 8px 20px;
    background: $corporateBlue;
    color: $white;
    border: none;
    border-radius: 4px;
    font-size: 1.25rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: lighten($corporateBlue, 6%);
    }

    &-count {
      opacity: 0.7;
      font-weight: 500;
    }
  }

  &__reset {
    padding: 8px 16px;
    background: none;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 1.25rem;
    font-weight: 600;
    color: $light_gray;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: #f5f6f8;
      color: $text;
    }
  }
}

.ff-group {
  border-bottom: 1px solid #f2f3f5;

  &:last-child {
    border-bottom: none;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 11px 0;
    background: none;
    border: none;
    cursor: pointer;
    color: $text;
    font-size: 1.3rem;
    font-weight: 600;
    text-align: left;
    transition: color 0.15s ease;

    &:hover {
      color: $corporateBlue;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: $corporateBlue;
    color: $white;
    border-radius: 9px;
    font-size: 1rem;
    font-weight: 700;
  }

  &__chevron {
    transition: transform 0.2s ease;
    flex-shrink: 0;
    color: $light_gray;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding-bottom: 10px;
    overflow: hidden;
  }

  &__empty {
    padding: 10px 0;
    color: $light_gray;
    font-size: 1.2rem;
    font-style: italic;
  }

  &__search {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 1.2rem;
    color: $text;
    margin-bottom: 6px;
    outline: none;

    &:focus {
      border-color: $corporateBlue;
    }

    &::placeholder {
      color: $border;
    }
  }

  &__options {
    max-height: 280px;
    overflow-y: auto;
    scrollbar-width: thin;
  }
}

.ff-item {
  &__row {
    display: flex;
    align-items: center;
    gap: 2px;
    min-height: 30px;
  }

  &__expand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: none;
    border: none;
    cursor: pointer;
    color: $light_gray;
    padding: 0;
    flex-shrink: 0;
    border-radius: 3px;
    transition: color 0.12s ease, background 0.12s ease;

    &:hover {
      color: $text;
      background: #f2f3f5;
    }
  }

  &__expand-icon {
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  &__expand-spacer {
    width: 20px;
    flex-shrink: 0;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    flex: 1;
    min-width: 0;
    padding: 2px 0;
  }

  &__checkbox {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    accent-color: $corporateBlue;
    cursor: pointer;
    margin: 0;

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  &__text {
    font-size: 1.2rem;
    color: $text;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--disabled {
      color: $light_gray;
    }
  }

  &__children {
    padding-left: 20px;
    overflow: hidden;
  }
}
</style>
